import { useState, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Package } from 'lucide-react';
import { toast } from '@/store/toastStore';
import {
  getPatientProductTypes,
  consumeProduct,
  consumeAppointmentProduct,
  deleteProduct,
  returnProduct,
  updateProductExpireDate,
  type ApptProductSlot,
} from '@/services/patientProductTypes.service';
import {
  appointmentsService,
  type Appointment,
} from '@/services/appointments.service';
import { AppointmentDetailModal } from '@/pages/appointments/AppointmentDetailModal';
import {
  ChargePaymentModal,
  type ChargePaymentPrefillItem,
} from '@/pages/payments/ChargePaymentModal';
import { useAuth } from '@/hooks/useAuth';
import { PERMISSION } from '@/lib/permissions';
import { useInViewport } from '@/hooks/useInViewport';
import { SectionHeader } from '@/components/patients/PatientDashboardSections';
import {
  ProductTypesDisplay,
  resolveTypeAndId,
  toArray,
  type ProductTypesResponse,
  type ProductGroup,
  type ActionHandler,
  type BatchConsumeHandler,
  type UpdateExpireDateHandler,
  type ProductItem,
} from './PatientProductsDisplay';

export function PatientProductsSection({ patientId, patientName }: { patientId: number | null; patientName?: string }) {
  const { hasMinUserLevel, hasPermission } = useAuth();
  const canEditExpireDate = hasMinUserLevel(3);
  // Deleting a product off a patient's available or on-hold stock. Level 4
  // clears it outright; below that the user needs the "Delete Products" grant.
  // Enforced again by the API — PatientProductTypesController::deleteProduct.
  const canDeleteProducts = hasMinUserLevel(4) || hasPermission(PERMISSION.DELETE_PRODUCTS);
  const { ref, inView } = useInViewport<HTMLDivElement>({ skip: !patientId });

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['patient-demographics-products', patientId],
    queryFn: () => getPatientProductTypes(String(patientId!)),
    enabled: inView && !!patientId,
    staleTime: 60_000,
  });

  // ── Charge / Purchase modal state ──────────────────────────────────────────
  const [chargeOpen, setChargeOpen] = useState(false);

  const productData = (data as ProductTypesResponse | undefined)?.data;
  const isMember = productData?.isMember ?? false;

  const purchasePrefill = useMemo<ChargePaymentPrefillItem[]>(() => {
    if (!productData) return [];
    const allGroups: ProductGroup[] = [
      ...Object.values(productData['product-types'] ?? {}),
      ...Object.values(productData['product-others'] ?? {}),
    ];

    type Group = {
      quoteItemCostId: number;
      quantity: number;
      productTypeName: string;
      scheduledAppointmentProductsIds: number[];
    };
    const byType = new Map<number, Group>();

    for (const g of allGroups) {
      const purchaseArr = toArray<ProductItem>(g.purchase);
      if (purchaseArr.length === 0) continue;
      const ptId = g.productTypeID ?? 0;
      const qicId = g.productID ? Number(g.productID) : 0;
      const name = g.productTypeName || g.productName || 'Unknown';

      for (const item of purchaseArr) {
        const sapId = item.scheduledAppointmentProductsID ?? 0;
        const itemQicId = (item.productID ? Number(item.productID) : 0) || qicId;
        const existing = byType.get(ptId);
        if (existing) {
          existing.quantity += item.quantity;
          if (!existing.quoteItemCostId && itemQicId) existing.quoteItemCostId = itemQicId;
          if (sapId > 0) existing.scheduledAppointmentProductsIds.push(sapId);
        } else {
          byType.set(ptId, {
            quoteItemCostId: itemQicId,
            quantity: item.quantity,
            productTypeName: name,
            scheduledAppointmentProductsIds: sapId > 0 ? [sapId] : [],
          });
        }
      }
    }

    return Array.from(byType.values());
  }, [productData]);

  // ── Appointment modal state ──────────────────────────────────────────────

  const [detailAppointment, setDetailAppointment] = useState<Appointment | null>(null);
  const [loadingApptId, setLoadingApptId] = useState<number | null>(null);

  const openAppointment = useCallback(async (appointmentId: number) => {
    setLoadingApptId(appointmentId);
    try {
      const fullAppointment = await appointmentsService.getAppointment(appointmentId);
      setDetailAppointment(fullAppointment);
    } catch {
      toast.error('Failed to load appointment details.');
    } finally {
      setLoadingApptId(null);
    }
  }, []);

  // ── Product action handlers ──────────────────────────────────────────────

  const handleAction: ActionHandler = useCallback(
    async (action: 'consume' | 'delete' | 'return', item: ProductItem, quantity?: number, skipRefetch?: boolean) => {
      if (!patientId) return;

      const resolved = resolveTypeAndId(item);
      if (!resolved) {
        toast.error('Cannot determine product source type');
        return;
      }

      try {
        if (action === 'consume') {
          await consumeProduct(String(patientId), { ...resolved, quantity });
        } else if (action === 'delete') {
          await deleteProduct(String(patientId), { ...resolved, quantity });
        } else if (action === 'return') {
          await returnProduct(String(patientId), {
            ...resolved,
            scheduledAppointmentProductsID: item.scheduledAppointmentProductsID ?? undefined,
          });
        }
        if (!skipRefetch) await refetch();
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Action failed';
        toast.error(msg);
      }
    },
    [patientId, refetch],
  );

  const handleBatchConsume: BatchConsumeHandler = useCallback(
    async (items: ProductItem[]) => {
      if (!patientId) return;

      const disbursable = items.filter((item) => resolveTypeAndId(item) !== null);
      if (disbursable.length === 0) {
        toast.error('No disbursable products in this group');
        return;
      }

      const apptId = disbursable[0].scheduledAppointmentsID;
      if (!apptId) {
        toast.error('No appointment ID found for these products');
        return;
      }

      const productJson: ApptProductSlot[] = disbursable.map((item) => ({
        scheduledAppointmentProductsID: String(item.scheduledAppointmentProductsID ?? ''),
        appointmentProductAddonsID: String(item.appointmentProductAddonsID ?? ''),
        productName: item.productName,
        quantity: item.quantity,
        type: '',
        quoteItemCostID: '',
        paymentMaterialID: String(item.paymentMaterialID ?? '0'),
        membershipProductID: String(item.membershipProductID ?? '0'),
        packageProductID: String(item.packageProductID ?? '0'),
        addedDate: item.addedDate ?? '',
      }));

      try {
        await consumeAppointmentProduct(String(patientId), {
          apptID: String(apptId),
          productJson,
        });
        toast.success(`Disbursed ${disbursable.length} product${disbursable.length > 1 ? 's' : ''} to appointment`);
        await refetch();
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Action failed';
        toast.error(`Failed to disburse: ${msg}`);
      }
    },
    [patientId, refetch],
  );

  // ── Expire date edit handler ─────────────────────────────────────────────

  const handleUpdateExpireDate: UpdateExpireDateHandler = useCallback(
    async (item: ProductItem, newExpireDate: string, quantity?: number) => {
      if (!patientId) return;

      const resolved = resolveTypeAndId(item);
      if (!resolved) {
        toast.error('Cannot determine product source type');
        return;
      }

      try {
        await updateProductExpireDate(String(patientId), {
          ...resolved,
          expireDate: newExpireDate,
          quantity,
        });
        toast.success('Expiration date updated');
        await refetch();
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to update expiration date';
        toast.error(msg);
      }
    },
    [patientId, refetch],
  );

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div ref={ref}>
      <SectionHeader icon={Package} label="Products" />

      {(!inView || isLoading) && !data && (
        <div className="space-y-2">
          <div className="h-10 rounded-lg bg-slate-100 animate-pulse" />
          <div className="h-10 rounded-lg bg-slate-100 animate-pulse" />
        </div>
      )}

      {isError && (
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          Failed to load products.
          <button
            type="button"
            className="ml-2 text-xs underline hover:text-red-900"
            onClick={() => refetch()}
          >
            Retry
          </button>
        </div>
      )}

      {data && (
        <>
          {loadingApptId && (
            <div className="mb-2 flex items-center gap-1.5 text-xs text-blue-600">
              <svg className="h-3.5 w-3.5 animate-spin" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Loading appointment...
            </div>
          )}
          <ProductTypesDisplay
            data={data as ProductTypesResponse}
            onAction={handleAction}
            onBatchConsume={handleBatchConsume}
            onOpenAppointment={openAppointment}
            onPurchase={() => setChargeOpen(true)}
            onRefetch={refetch}
            onUpdateExpireDate={handleUpdateExpireDate}
            canEditExpireDate={canEditExpireDate}
            canDeleteProducts={canDeleteProducts}
          />
        </>
      )}

      {detailAppointment && (
        <AppointmentDetailModal
          isOpen={Boolean(detailAppointment)}
          onClose={() => {
            setDetailAppointment(null);
            refetch();
          }}
          appointment={detailAppointment}
          onUpdated={(updatedAppointment) => {
            setDetailAppointment(updatedAppointment);
          }}
        />
      )}

      {chargeOpen && patientId && (
        <ChargePaymentModal
          open={chargeOpen}
          onClose={() => setChargeOpen(false)}
          patientId={patientId}
          patientName={patientName}
          prefillItems={purchasePrefill}
          isMember={isMember}
          onSaved={() => {
            setChargeOpen(false);
            refetch();
          }}
        />
      )}
    </div>
  );
}
